import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, FormLabel, Button } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import InfoBar from '../components/Navigation/InfoBar';
import useApi from '../hooks/use-api';
import { getCertificatesForCompany } from '../constants/routes';
import DateInput from '../components/Form/DateInput';
import MultiSelect from '../components/common/MultiSelect';
import CcvCodes from '../utils/code95.json';
import { getDisplayName } from '../utils/person';
import { getRisk } from '../utils/training';
import useUserPreferences from '../hooks/use-user-preferences';
import { ESTABLISHMENT_FILTER_KEY } from '../components/common/EstablishmentFilter';
import { useUser } from '../hooks/use-user';
import useEstablishments from '../hooks/use-establishments';
import CertificatesOverview from '../components/Tables/Certificates/CertificatesOverview';

export default function CertificatePage() {
	const establishments = useEstablishments();
	const { activeCompany } = useUser();
	const { t } = useTranslation();
	const { getFilters, setFilters } = useUserPreferences();
	const FILTER_NAME = 'COURSE_LIST_PAGE_FILTERS';

	const [{ data }, fetchCertificates] = useApi.get(
		getCertificatesForCompany(activeCompany.id),
		{},
		{ lazy: true },
	);

	const [selectedCcvCodes, setSelectedCcvCodes] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [certificates, setCertificates] = useState([]);

	const search = useCallback(() => {
		fetchCertificates({
			...(selectedCcvCodes?.length > 0
				? { ccvCodes: selectedCcvCodes.map(({ value }) => value) }
				: {}),
			...(moment.isMoment(selectedDate)
				? { certificateExpiresBeforeDate: selectedDate?.format() }
				: {}),
		});

		setFilters(FILTER_NAME, {
			selectedCcvCodes,
			selectedDate: moment.isMoment(selectedDate) ? selectedDate?.format() : null,
		});
	}, [selectedCcvCodes, selectedDate]);

	useEffect(() => {
		const { selectedCcvCodes: rememberedCodes, selectedDate: rememberedDate } =
			getFilters(FILTER_NAME) || {};
		fetchCertificates({
			...(rememberedCodes?.length > 0
				? { ccvCodes: rememberedCodes.map(({ value }) => value) }
				: {}),
			...(rememberedDate ? { certificateExpiresBeforeDate: moment(rememberedDate)?.format() } : {}),
		});
		setSelectedCcvCodes(rememberedCodes);
		setSelectedDate(rememberedDate ? moment(rememberedDate) : null);
	}, []);

	useEffect(() => {
		if (data) {
			setCertificates(
				data
					.filter(
						(cert) =>
							!cert.student.establishment?.id ||
							establishments.find((est) => est.id === cert.student.establishment.id),
					)
					.map((cert) => {
						const expiryDateMoment = moment(cert.certificateExpirationDate);
						return {
							studentId: cert.student.id,
							studentName: getDisplayName(cert.student),
							trainingName: cert.training.name,
							certificateExpirationDate: expiryDateMoment.format('L'),
							sortDate: expiryDateMoment.format(),
							risk: getRisk(expiryDateMoment),
							[ESTABLISHMENT_FILTER_KEY]: cert.student.establishment?.id,
						};
					}),
			);
		}
	}, [data, establishments]);

	return (
		<>
			<InfoBar />
			<div className="col-sm-12 col-xl-10 mx-auto pb-4">
				<Row className="align-items-end mb-3">
					<Col xs={4}>
						<FormLabel>{t('certificates.filter.courseSelectLabel')}</FormLabel>
						<MultiSelect
							value={selectedCcvCodes}
							options={CcvCodes.map(({ ccvCode, description }) => ({
								label: `${ccvCode} - ${description}`,
								value: ccvCode,
							}))}
							onChange={setSelectedCcvCodes}
							maxItemsShown={2}
						/>
					</Col>
					<Col xs={3}>
						<FormLabel>{t('certificates.filter.expiryDateLabel')}</FormLabel>
						<DateInput
							onChange={(newDateMoment) => setSelectedDate(newDateMoment)}
							value={selectedDate}
						/>
					</Col>
					<Col xs={2}>
						<Button variant="primary" onClick={search}>
							{t('certificates.filter.search')}
						</Button>
					</Col>
				</Row>
				<Row className="mt-3">
					<CertificatesOverview certificates={certificates} />
				</Row>
			</div>
		</>
	);
}
